import type { ArtworkGateway, AcceptedPrintFile, UploadRejectionCode } from "../../../packages/pvc-product/artwork-controller";
import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../packages/pvc-product/shop-api-transport";

const UPLOAD = `mutation DesignerUploadArtwork($file: Upload!) { uploadHappyPrintPreorderArtwork(file: $file) { id filename mimeType sizeBytes expiresAt } }`;
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid artwork response");
  return value as Record<string, unknown>;
}
function accepted(value: unknown): AcceptedPrintFile {
  const file = object(value);
  if (typeof file.id !== "string" || !file.id || typeof file.filename !== "string" || !file.filename) throw new Error("Invalid artwork file");
  return { id: file.id, filename: file.filename };
}
const REJECTIONS = new Set<UploadRejectionCode>(["UNSUPPORTED_FILE_TYPE", "FILE_TOO_LARGE", "CORRUPT_FILE", "FILE_SIGNATURE_MISMATCH", "FILE_VALIDATION_FAILED", "PROCESSING_FAILED"]);

export function createPreorderArtworkGateway(client: ShopApiGraphqlClient, options: { endpoint: string; fetchImpl?: typeof fetch }): ArtworkGateway {
  const send = options.fetchImpl ?? globalThis.fetch;
  return {
    async uploadFile(file, { signal, onProgress }) {
      if (!file.size || file.size > 25 * 1024 * 1024) return { type: "rejected", code: file.size ? "FILE_TOO_LARGE" : "CORRUPT_FILE" };
      // Establish the same Owner session used by the existing cart transport.
      await client.execute({ operationName: "DesignerArtworkSession", document: "query DesignerArtworkSession { activeOrder { id } }", signal });
      const form = new FormData();
      form.append("operations", JSON.stringify({ operationName: "DesignerUploadArtwork", query: UPLOAD, variables: { file: null } }));
      form.append("map", JSON.stringify({ "0": ["variables.file"] }));
      form.append("0", file, file.name);
      onProgress?.(0);
      const response = await send(options.endpoint, { method: "POST", credentials: "include", headers: { "Apollo-Require-Preflight": "true" }, body: form, signal });
      if (response.status === 413) return { type: "rejected", code: "FILE_TOO_LARGE" };
      if (!response.ok) throw new Error("Artwork upload failed; no automatic retry");
      const envelope = object(await response.json());
      if (Array.isArray(envelope.errors) && envelope.errors.length) {
        const message = object(envelope.errors[0]).message;
        if (typeof message === "string" && REJECTIONS.has(message as UploadRejectionCode)) return { type: "rejected", code: message as UploadRejectionCode };
        throw new Error("Artwork upload returned an error");
      }
      const result = accepted(object(envelope.data).uploadHappyPrintPreorderArtwork);
      onProgress?.(1);
      return { type: "accepted", file: result };
    },
    async verifyPrintFile(id) {
      const data = object(await client.execute({ operationName: "DesignerVerifyArtwork", document: "query DesignerVerifyArtwork($id: ID!) { verifyHappyPrintPreorderArtwork(id: $id) { valid file { id filename } } }", variables: { id } }));
      const result = object(data.verifyHappyPrintPreorderArtwork);
      if (result.valid !== true) return { valid: false };
      const file = accepted(result.file);
      if (file.id !== id) throw new Error("Artwork identity mismatch");
      return { valid: true, file };
    },
    async removePrintFile(id) {
      const data = object(await client.execute({ operationName: "DesignerRemoveArtwork", document: "mutation DesignerRemoveArtwork($id: ID!) { removeHappyPrintPreorderArtwork(id: $id) }", variables: { id } }));
      if (data.removeHappyPrintPreorderArtwork !== true) throw new Error("Claimed or unavailable artwork was not removed");
    },
    async createUploadSession() { throw new Error("Cross-device upload is unavailable"); },
    async getUploadSessionStatus() { throw new Error("Cross-device upload is unavailable"); },
    async cancelUploadSession() {},
    async assessArtwork() { return []; },
  };
}


export function createBrowserPreorderArtworkGateway(): ArtworkGateway {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  return createPreorderArtworkGateway(
    createShopApiGraphqlClient({ endpoint }),
    { endpoint },
  );
}
